import type { LegacyWorkTaskStatus, WorkTaskApproval, WorkTaskKind, WorkTaskStatus } from './work-task-types.js';

export const WORK_TASK_STATUSES: readonly WorkTaskStatus[] = [
  'raised',
  'acknowledged',
  'approved',
  'in-implementation',
  'implemented',
  'test-staging',
  'ready-for-production',
  'production-completed',
];
export type WorkflowTask = {
  status: WorkTaskStatus | LegacyWorkTaskStatus;
  workflowStatus?: WorkTaskStatus;
  kind?: WorkTaskKind;
  estimateRevision: number;
  approval: WorkTaskApproval | null;
  revision?: number;
  technicalProgress?: { status: WorkTaskStatus; revision: number };
  productionCompletion?: { at: string; byUserID: string } | null;
};
export function hasCurrentTaskApproval(task: Pick<WorkflowTask, 'approval' | 'estimateRevision'>): boolean {
  return !!task.approval && task.approval.estimateRevision === task.estimateRevision;
}
function hasProductionCompletion(task: WorkflowTask): boolean {
  const evidence = task.productionCompletion;
  return (
    !!evidence &&
    typeof evidence.byUserID === 'string' &&
    evidence.byUserID.trim().length > 0 &&
    evidence.byUserID.length <= 256 &&
    !/[\u0000-\u001f\u007f]/.test(evidence.byUserID) &&
    typeof evidence.at === 'string' &&
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(evidence.at) &&
    Number.isFinite(Date.parse(evidence.at)) &&
    new Date(evidence.at).toISOString() === evidence.at
  );
}
/** Legacy in-progress means acknowledged, never completed. No business approval is invented. */
export function taskWorkflowStatus(task: WorkflowTask): WorkTaskStatus {
  // Older initializers can rewrite workflowStatus without changing revision. Preserve
  // explicit progress across that metadata-only rewrite, but never after a legacy edit.
  const progress = task.technicalProgress;
  if (
    progress &&
    progress.revision === task.revision &&
    WORK_TASK_STATUSES.includes(progress.status) &&
    (task.status === progress.status || task.status === legacyTaskStatus(progress.status)) &&
    (progress.status !== 'production-completed' || hasProductionCompletion(task)) &&
    (progress.status !== 'approved' || hasCurrentTaskApproval(task))
  )
    return progress.status;
  const canonical = task.workflowStatus;
  // A rolled-back application can change the legacy status without knowing workflowStatus.
  // Trust the more detailed stage only while the coarse legacy status still agrees with it.
  let mapped: WorkTaskStatus;
  if (
    canonical &&
    WORK_TASK_STATUSES.includes(canonical) &&
    canonical !== 'production-completed' &&
    (task.status === canonical || task.status === legacyTaskStatus(canonical))
  ) {
    mapped = canonical;
  } else if (task.status === 'production-completed' && !task.workflowStatus && hasProductionCompletion(task)) {
    // Public transport has the derived status, without the internal revision ledger.
    mapped = 'production-completed';
  } else if (WORK_TASK_STATUSES.includes(task.status as WorkTaskStatus) && task.status !== 'production-completed') {
    mapped = task.status as WorkTaskStatus;
  } else {
    mapped =
      (
        {
          pending: 'raised',
          'in-progress': 'acknowledged',
          'ready-for-review': 'implemented',
          completed: 'ready-for-production',
        } as const
      )[task.status as LegacyWorkTaskStatus] || 'raised';
  }
  const approved = hasCurrentTaskApproval(task);
  // Technical progress and commercial decisions are independent. The explicit
  // approval stage still requires its original, current approval evidence.
  if (!approved && mapped === 'approved') return 'acknowledged';
  return approved && (mapped === 'raised' || mapped === 'acknowledged') ? 'approved' : mapped;
}
/** Keep a compatible coarse status for a rollback to the previous application image. */
export function legacyTaskStatus(status: WorkTaskStatus): LegacyWorkTaskStatus {
  return (
    {
      raised: 'pending',
      acknowledged: 'in-progress',
      approved: 'in-progress',
      'in-implementation': 'in-progress',
      implemented: 'ready-for-review',
      'test-staging': 'ready-for-review',
      'ready-for-production': 'completed',
      'production-completed': 'completed',
    } as const
  )[status];
}
export function taskKind(value: unknown, allowUnclassified = false): WorkTaskKind {
  if (value === 'bug' || value === 'feature' || (allowUnclassified && value === 'unclassified')) return value;
  throw new Error('TASK_INVALID_INPUT');
}
export function taskCalendarDate(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) throw new Error('TASK_INVALID_INPUT');
  const parsed = new Date(value + 'T00:00:00.000Z');
  if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value || value.startsWith('0000-'))
    throw new Error('TASK_INVALID_INPUT');
  return value;
}
export function taskTransition(
  task: WorkflowTask,
  status: WorkTaskStatus,
): {
  workflowStatus: WorkTaskStatus;
  status: LegacyWorkTaskStatus;
  approval?: null;
} {
  if (!WORK_TASK_STATUSES.includes(status)) throw new Error('TASK_INVALID_INPUT');
  if (status === 'approved' && !hasCurrentTaskApproval(task)) throw new Error('TASK_APPROVAL_REQUIRED');
  const advanced = [
    'in-implementation',
    'implemented',
    'test-staging',
    'ready-for-production',
    'production-completed',
  ].includes(status);
  if (advanced && (!task.kind || task.kind === 'unclassified')) throw new Error('TASK_CLASSIFICATION_REQUIRED');
  return {
    // A rollback can still render this task; explicit completion is kept additively.
    workflowStatus: status === 'production-completed' ? 'ready-for-production' : status,
    status: legacyTaskStatus(status),
    ...(status === 'raised' || status === 'acknowledged' ? { approval: null } : {}),
  };
}
